import React, { useEffect, useState } from "react";
import { getAuth, signInWithEmailAndPassword } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { Box, Button, TextField, Typography, Alert, CircularProgress, IconButton, InputAdornment } from "@mui/material";
import "src/firebase/firebase";
import useAuth from "src/context/AuthContext/useAuth";
import { EyeIcon } from "src/components/EyeIconComponents/EyeIcon";
import { EyeSlashIcon } from "src/components/EyeIconComponents/EyeSlashIcon";

const SIMULATOR_TYPE = process.env.REACT_APP_SIMULATOR_TYPE || "target";
const SIMULATOR_LABEL = SIMULATOR_TYPE.charAt(0).toUpperCase() + SIMULATOR_TYPE.slice(1).toLowerCase();

const Login: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const auth = getAuth();
  const { accessError, isLoggedIn } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoggedIn) navigate("/", { replace: true });
  }, [isLoggedIn]);

  useEffect(() => {
    if (accessError) setError(accessError);
  }, [accessError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const credential = await signInWithEmailAndPassword(auth, email, password);
      const token = await credential.user.getIdToken(true);
      localStorage.setItem("id_token", token);
    } catch {
      setError("Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      {/* Left — form */}
      <Box
        sx={{
          width: 600,
          flexShrink: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          bgcolor: "#fff",
        }}
      >
        <Box sx={{ width: 416, textAlign: "center", color: "#6c6c6c" }}>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <img
              src={require("src/assets/images/Strawbay-logo-black.png")}
              alt="Strawbay logo"
              style={{ height: 46, width: "auto" }}
            />
          </Box>

          <Typography variant="h5" fontWeight={700} fontFamily="Maven Pro, sans-serif" color="#6c6c6c"
            sx={{ pt: "85px", mb: "19px" }}>
            Strawbay {SIMULATOR_LABEL} Simulator
          </Typography>
          <Typography variant="body2" fontFamily="Maven Pro, sans-serif" color="#6c6c6c" sx={{ pb: "11px" }}>
            Sign in with your Strawbay account
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              label="Password"
              type={showPassword ? "text" : "password"}
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              sx={{ mb: "25px" }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{
                height: 40,
                bgcolor: "#7166cf",
                "&:hover": { bgcolor: "#5a4dbd" },
                fontFamily: "Maven Pro, sans-serif",
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : "Log in"}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Right — background image */}
      <Box
        sx={{
          flex: 1,
          backgroundImage: `url(${require("src/assets/images/login-background.png")})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />
    </Box>
  );
};

export default Login;
